import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/home";

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const touchStart = useRef(0);
  const total = heroSlides.length;

  const go = (index: number) => setCurrent((index + total) % total);

  useEffect(() => {
    const timer = setInterval(() => setCurrent((v) => (v + 1) % total), 5000);
    return () => clearInterval(timer);
  }, [total]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const delta = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 50) go(delta > 0 ? current - 1 : current + 1);
  };

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-foreground-950">
      {heroSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} 动漫资讯`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/55 to-foreground-950/20"></div>
        </div>
      ))}

      <div
        className="relative z-10 h-full w-full px-4 md:px-8 flex items-center"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="w-full max-w-2xl">
          <div key={current} className="animate-float-in">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs md:text-sm font-medium">
              <i className="ri-fire-fill"></i>
              {heroSlides[current].tag}
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-black text-white mt-4 leading-tight">
              {heroSlides[current].title}
            </h1>
            <p className="text-sm md:text-lg text-background-200 mt-4 leading-relaxed max-w-xl">
              {heroSlides[current].desc}
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-6 md:mt-8">
              <Link
                to={heroSlides[current].link}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                查看详情
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/works"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-background-50/15 hover:bg-background-50/25 text-white text-sm font-medium border border-white/30 whitespace-nowrap transition-colors cursor-pointer"
              >
                浏览作品库
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 md:bottom-7 left-4 md:left-8 z-10 flex items-center gap-2">
        {heroSlides.map((slide, index) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第 ${index + 1} 张`}
            onClick={() => go(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === current ? "w-8 bg-primary-500" : "w-4 bg-white/50 hover:bg-white/80"
            }`}
          />
        ))}
      </div>

      <div className="hidden md:flex absolute bottom-7 right-8 z-10 items-center gap-2">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => go(current - 1)}
          className="w-10 h-10 rounded-full bg-background-50/15 hover:bg-background-50/30 text-white border border-white/30 flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => go(current + 1)}
          className="w-10 h-10 rounded-full bg-background-50/15 hover:bg-background-50/30 text-white border border-white/30 flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>
    </section>
  );
}